"use client"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { FormEvent, useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { isNumeric } from "../../../../util/string"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { formatCoins } from "../../../../util/format"
import { useCloseSupport } from "./SupportSheetDrawer"
import { toast } from "sonner"
import { sellCoins } from "@/lib/api/supporters"

type Props = {
  initialCoins: number
  coinValueInETH: number
  coinValueInUSD: number
}

export default function Trade({
  initialCoins,
  coinValueInETH,
  coinValueInUSD,
}: Props) {
  const [coins, setCoins] = useState(String(0))
  const [loading, setLoading] = useState(false)
  const { close } = useCloseSupport()
  const isValidCoins =
    isNumeric(coins) &&
    parseFloat(coins) <= initialCoins &&
    parseFloat(coins) > 0 &&
    parseFloat(coins) === parseInt(coins)

  const handleClick = (percentage: number) => {
    if (initialCoins > 0) {
      setCoins(String(Math.floor((initialCoins * percentage) / 100)))
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!isValidCoins) return
    try {
      const promise = sellCoins(parseFloat(coins))

      toast.promise(promise, {
        success: ({ coins, usdValue }) =>
          `Your trade went through — ${coins} coins exchanged for $${usdValue}.`,
        error:
          "We couldn’t complete your trade. Please check your wallet and try again.",
      })
      await promise
    } finally {
      setLoading(false)
      close()
    }
  }

  return (
    <form className="mt-6" onSubmit={handleSubmit}>
      <div className="w-full flex gap-4 my-6">
        <Card className="flex-1 py-4 px-8">
          <CardContent className="flex flex-col items-center justify-center p-0 gap-1">
            <h3 className="font-normal text-wood-500 text-xs">Balance</h3>
            <span className="font-normal text-sm text-foreground">
              {formatCoins(initialCoins)}
            </span>
          </CardContent>
        </Card>

        <Card className="flex-1 py-4 px-8 flex justify-center">
          <Tooltip delayDuration={250}>
            <TooltipTrigger>
              <CardContent className="flex flex-col items-center justify-center p-0 gap-1">
                <h3 className="font-normal text-wood-500 text-xs">Value</h3>
                {/* TODO: Add real value */}
                <div className="flex flex-col items-center">
                  <span className="font-normal text-sm text-foreground">
                    {`$${(coinValueInUSD * initialCoins).toFixed(2)}`}
                  </span>
                  <span className="font-normal text-xs text-foreground">
                    {`(${(coinValueInETH * initialCoins).toFixed(ETH_DECIMALS)} ETH)`}
                  </span>
                </div>
              </CardContent>
            </TooltipTrigger>

            <TooltipContent className="max-w-[280px] bg-card p-4 z-[51] flex flex-col gap-2 text-card-foreground border rounded-md text-sm">
              This is an approximate value based on the coins you hold and the
              current market price. The actual amount you receive may be lower,
              as the price can change when coins are sold.
            </TooltipContent>
          </Tooltip>
        </Card>
      </div>

      <h3 className="font-normal text-sm text-wood-500">
        How many coins would you like to sell?
      </h3>

      <div className="relative mt-5">
        <Input
          value={coins}
          onChange={(val) => setCoins(val.target.value)}
          className="px-4 py-6 text-xl font-normal border-border"
        />
        <span className="absolute right-2 top-0 pt-[10px] pr-2 text-xl font-normal text-muted-foreground pointer-events-none">
          coins
        </span>
      </div>

      <div className="flex gap-3 my-4">
        <Button
          className="flex-1"
          variant="outline"
          onClick={() => handleClick(25)}
        >
          25%
        </Button>
        <Button
          className="flex-1"
          variant="outline"
          onClick={() => handleClick(50)}
        >
          50%
        </Button>
        <Button
          className="flex-1"
          variant="outline"
          onClick={() => handleClick(75)}
        >
          75%
        </Button>
        <Button
          className="flex-1"
          variant="outline"
          onClick={() => handleClick(100)}
        >
          100%
        </Button>
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={!isValidCoins || loading}
      >
        Trade
      </Button>

      <div className="bg-wood-50 dark:bg-background dark:border-border border-transparent border rounded-lg mt-5 p-3 flex flex-col gap-3">
        <div className="flex w-full justify-between items-center">
          <span className="text-sm font-normal text-muted-foreground">
            Receive
          </span>
          {isValidCoins && (
            <div className="flex items-center gap-1">
              <span className="text-sm font-normal text-foreground">{`$${(parseFloat(coins) * coinValueInUSD).toFixed(2)}`}</span>
              <span className="text-sm font-normal text-muted-foreground">
                {`(${(parseFloat(coins) * coinValueInETH).toFixed(ETH_DECIMALS)} ETH)`}
              </span>
            </div>
          )}
        </div>
        <div className="flex w-full justify-between">
          <span className="text-sm font-normal text-muted-foreground">
            Remaining
          </span>
          <span className="text-sm font-normal text-foreground">
            {isValidCoins || parseFloat(coins) === 0
              ? formatCoins(initialCoins - parseFloat(coins))
              : ""}
          </span>
        </div>
      </div>
    </form>
  )
}

const ETH_DECIMALS = 5
